/**
 * Site Audit tab — User-Authored Conflicts subsection (UAE-CONFLICT EDX-10).
 */
import React from 'react';
import type { SiteEntity } from '../../../types/site';
import type { UserAuthoredConflict } from '../../../lib/userAmendmentEngine';

function statusTone(status: UserAuthoredConflict['status']): string {
  return status === 'resolved'
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : 'bg-amber-50 text-amber-800 border-amber-200';
}

export function UserAuthoredConflictsPanel({
  site,
}: {
  site: SiteEntity;
}) {
  const conflicts = site.userAuthoredConflicts ?? [];
  if (conflicts.length === 0) return null;

  const unresolved = conflicts.filter((c) => c.status === 'unresolved').length;

  return (
    <section className="space-y-3" data-testid="user-authored-conflicts-panel">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary">
          User-Authored Conflicts
        </h3>
        <span className="text-xs text-text-muted" data-testid="user-authored-conflicts-count">
          {unresolved} unresolved · {conflicts.length} total
        </span>
      </div>
      <div className="space-y-2">
        {conflicts.map((c) => (
          <div
            key={c.id}
            className="border border-border rounded-lg p-3 bg-background text-sm"
            data-testid={`user-authored-conflict-${c.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium text-text-primary">{c.note}</p>
              <span className={`shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded ${statusTone(c.status)}`}>
                {c.status}
              </span>
            </div>
            <ul className="mt-2 space-y-1 text-xs text-text-secondary">
              {c.sources.map((s, i) => (
                <li key={`${c.id}-src-${i}`}>
                  <span className="font-mono">{s.value}</span>
                  <span className="text-text-muted"> · {s.sourceRef}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-text-muted">
              Flagged by {c.authoredBy.email ?? c.authoredBy.uid}
              {c.sedField ? ` · ${c.sedField}` : ''}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
